import { Anchor } from "@svea/design-system-components-react";
import { Heading, P, Pill, Stack, tw, useText } from "@sveabank/cc-common";
import { Text } from "@sveabank/cc-common";
import { Link } from "react-router";
import Card from "../../components/card";
import Image from "../../components/image";
import { useApplicationDetails } from "../../hooks/application";
import { usePageContext } from "../../hooks/context";
import type { DisplayedAddon } from "../../types/product";
import AddonItemActions from "./addon-item-actions";
import AddonItemPrice from "./addon-item-price";
import { isCreditWorthy } from "./helpers";
import { useChoosePackage } from "./hooks";

type Props = {
	addon: DisplayedAddon;
};

const creditProducts = ["CREDITCARD", "CHECKKREDIT"];

const AddonItemCheckCredit = ({ addon }: Props) => {
	const [t, texts] = useText();
	const { selectedAddons, creditScore } = usePageContext(useChoosePackage);
	const { applicationDetails } = useApplicationDetails();
	const isRejected = !creditScore?.assessment || !isCreditWorthy(creditScore.assessment);
	const unavailable = isRejected && creditProducts.includes(addon.definitionId);

	const interestPromotion = applicationDetails?.promotions.find(
		(p) => p.promotionType === "interestRateDiscount",
	);

	const isSelected = selectedAddons?.some((a) => a.productDefinitionId === addon.definitionId);
	const addonTexts = texts.addons[addon.sku];
	const externalLink = "externalLink" in addonTexts ? t(addonTexts.externalLink) : "";
	const moreAboutText = "moreAbout" in addonTexts ? t(addonTexts.moreAbout) : "";
	const description = "description" in addonTexts ? t(addonTexts.description) : "";

	return (
		<div className="relative h-full" data-testid={`addon-card-${addon.definitionId}`}>
			<AddonItemPrice addon={addon} />

			<Card
				direction="col"
				classNames={tw(
					"h-full p-0",
					"bg-background-primary",
					isSelected && "outline-interactive-primary outline-3 outline-solid",
				)}
			>
				<Stack gap={6}>
					<Image
						className="h-auto w-full self-center rounded-t-md"
						imageSrc="CHECK_CREDIT"
						altText={t(addonTexts.title)}
					/>

					<div className="flex h-full flex-col justify-between gap-4 px-4 pb-6">
						{interestPromotion && addon.definitionId === "CHECKKREDIT" && (
							<Pill
								status="info"
								text={`Din kampanjränta ${creditScore.assessment?.check.interestRate} %`}
							/>
						)}
						<Stack>
							<Stack gap={1}>
								<Heading.H4 className="text-text-primary">{t(addonTexts.title)}</Heading.H4>

								<P>{description}</P>
								<Anchor as={Link} to={externalLink} target="_blank" iconEnd="externalLink">
									{moreAboutText}
								</Anchor>

								{unavailable && <Text className="pt-4">{t(texts.addons.unavailable)} </Text>}
							</Stack>
						</Stack>

						<div className="self-end">
							<Stack align="end">{!unavailable && <AddonItemActions addon={addon} />}</Stack>
						</div>
					</div>
				</Stack>
			</Card>
		</div>
	);
};

export default AddonItemCheckCredit;
